import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Grid, MenuItem, TextField, Typography, Slider, Radio, RadioGroup, FormControlLabel } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import InfoIcon from "@mui/icons-material/Info";
import { jobStatuses, jobRegions, offerLanguages, frenchRegions, languages, seniorityLevels, billableHours, barStatusOptions } from "bundles/common/utils/constants";
import { capitalize, formatRegion, formatSalaryInternal, organizeCategoriesByDepartment, debounce } from "bundles/common/utils/helpers";
import stepstoneMappings from "bundles/common/utils/mappings/stepstone";
import sh from "bundles/common/utils/sh";
import CardSelect from "bundles/Internal/components/CardSelect";
import TextEditor from "bundles/Internal/components/TextEditor";

const Informations = ({ jobOffer, values, setValues, handleNextStep }) => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const [errors, setErrors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [charCount, setCharCount] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [jobTitleCharCount, setJobTitleCharCount] = useState(0);
  const [companyInputValue, setCompanyInputValue] = useState(jobOffer?.company?.name ? jobOffer.company.name : "");
  const [companyValue, setCompanyValue] = useState(jobOffer?.company ? jobOffer.company : null);
  const isInitialMount = useRef(true);

  const MAX_CHAR_LIMIT_SUMMARY = 160;
  const MAX_CHAR_LIMIT_JOB_TITLE = 62;

  const categoriesByDepartment = organizeCategoriesByDepartment(categories);

  const isLegalJob = () => {
    if (!values.category_id) return false;
    const selectedCategory = categories.find(cat => cat.id === values.category_id);
    return selectedCategory?.department?.code === "le";
  };

  const fetchAllCategories = () => {
    sh.get("internal/all_categories").then((res) => {
      setCategories(res?.data?.categories || []);
    });
  };

  const fetchCompanies = (query) => {
    sh.get(`internal/companies/search?query=${encodeURIComponent(query)}`).then((res) => {
      setCompanies(res?.data?.companies || []);
    });
  };

  const debouncedFetchCompanies = useCallback(
    debounce((query) => {
      fetchCompanies(query);
    }, 300),
    []
  );

  const handleCompanySelection = (event, newValue) => {
    if (newValue && newValue.id) {
      setCompanyValue(newValue);
      setCompanyInputValue(newValue.name);
      setValues({ ...values, company_id: newValue.id });
      validate({ company_id: newValue.id });
    } else if (newValue && newValue.inputValue) {
      createCompany(newValue.inputValue);
    } else {
      setCompanyValue(null);
      setCompanyInputValue("");
      setValues({ ...values, company_id: null });
      validate({ company_id: null });
    }
  };

  const handleCompanyInputChange = (event, newInputValue) => {
    setCompanyInputValue(newInputValue);

    if (companyValue || values.company_id) {
      setCompanyValue(null);
      setValues((prevValues) => ({ ...prevValues, company_id: null }));
    }

    if (newInputValue) {
      debouncedFetchCompanies(newInputValue);
    } else {
      setCompanies([]);
    }
  };

  const createCompany = (companyName) => {
    sh.post("/internal/companies", {
      authenticity_token: authenticityToken,
      name: companyName
    })
      .then((response) => {
        const newCompany = response.data.company;
        setCompanies((prevCompanies) => [...prevCompanies, newCompany]);
        setValues({ ...values, company_id: newCompany.id });
        setCompanyValue(newCompany);
        setCompanyInputValue(newCompany.name);
        validate({ company_id: newCompany.id });
      })
      .catch((error) => {
        console.error("Error creating company:", error);
        setErrors((prevErrors) => ({
          ...prevErrors,
          company_id: "Failed to create company"
        }));
      });
  };

  const handleSalaryTypeChange = (event) => {
    const newSalaryType = event.target.value;
    setValues({
      ...values,
      salary_type: newSalaryType,
      min_salary: newSalaryType === "no-salary" ? null : 0,
      max_salary: newSalaryType === "no-salary" ? null : 0,
    });
  };

  const handleSliderChange = (event, newValue) => {
    setValues({
      ...values,
      min_salary: newValue[0].toString(),
      max_salary: newValue[1].toString(),
    });
    validate({ min_salary: newValue[0].toString(), max_salary: newValue[1].toString() });
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    let newValues = { ...values, [name]: value };

    // Prevent employee_count from going below 1
    if (name === "employee_count" && value !== "" && Number(value) < 1) {
      return; // Don't update if value is less than 1
    }

    if (name === "region") {
      const geographyId = stepstoneMappings.GEOGRAPHY_IDS[value];
      const isFrenchJob = frenchRegions.includes(value);

      newValues = {
        ...newValues,
        stepstone_data: isFrenchJob
          ? {}
          : {
            ...newValues.stepstone_data,
            geography: geographyId || null,
          }
      };
    }

    setValues(newValues);
    validate({ [name]: value });

    if (name === "job_title") {
      setJobTitleCharCount(value.length);
    }
  };

  const handleCardSelected = (name, value, multiple) => {
    if (multiple) {
      if (values[name].includes(value)) {
        setValues({
          ...values,
          [name]: values[name].filter((v) => v != value),
        });
      } else {
        setValues({ ...values, [name]: (values[name] || []).concat([value]) });
        validate({ [name]: (values[name] || []).concat([value]) });
      }
    } else {
      setValues({ ...values, [name]: value });
      validate({ [name]: value });
    }
  };

  const handleTextEditor = (fieldName, value) => {
    setValues({ ...values, [fieldName]: value });
    validate({ [fieldName]: value });
  };

  const strip = (htmlString) => {
    if (!htmlString) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = htmlString;
    return tmp.textContent || tmp.innerText || "";
  };

  const getSliderMax = (salaryType) => {
    switch (salaryType) {
    case "yearly":
      return 200;
    case "monthly":
      return 20;
    default:
      return 1000;
    }
  };

  const getSliderStep = (salaryType) => {
    switch (salaryType) {
    case "yearly":
      return 1;
    case "monthly":
      return 0.1;
    default:
      return 5;
    }
  };

  const validate = (fieldValues = {}) => {
    let temp = { ...errors };
    let required = "Required";

    if ("offer_language" in fieldValues) {
      temp.offer_language = fieldValues.offer_language ? "" : required;
    }

    if ("category_id" in fieldValues) {
      temp.category_id = fieldValues.category_id ? "" : required;
    }

    if ("job_title" in fieldValues) {
      temp.job_title = fieldValues.job_title ? "" : required;
      if (fieldValues.job_title && fieldValues.job_title.length > MAX_CHAR_LIMIT_JOB_TITLE) {
        temp.job_title = `Limit exceeded by ${fieldValues.job_title.length - MAX_CHAR_LIMIT_JOB_TITLE} character(s)`;
      }
    }

    if ("company_id" in fieldValues) {
      temp.company_id = fieldValues.company_id ? "" : required;
    }

    if ("job_status" in fieldValues) {
      temp.job_status = fieldValues.job_status ? "" : required;
    }

    if ("region" in fieldValues) {
      temp.region = fieldValues.region ? "" : required;
    }

    if ("city" in fieldValues) {
      temp.city = fieldValues.city ? "" : required;
    }

    if ("languages" in fieldValues) {
      temp.languages = fieldValues.languages?.length > 0 ? "" : required;
    }

    if ("salary_type" in fieldValues || "min_salary" in fieldValues || "max_salary" in fieldValues) {
      if (fieldValues.salary_type === "no-salary") {
        temp.salary = "";
      } else if (!fieldValues.min_salary || !fieldValues.max_salary || Number(fieldValues.min_salary) === 0 && Number(fieldValues.max_salary) === 0) {
        temp.salary = "Please enter a value or select 'No salary'";
      } else {
        temp.salary = "";
      }
    }

    if ("summary" in fieldValues) {
      let summaryStripped = strip(fieldValues.summary);
      if (!summaryStripped) {
        temp.summary = required;
      } else if (summaryStripped.length > MAX_CHAR_LIMIT_SUMMARY) {
        temp.summary = `Limit exceeded by ${summaryStripped.length - MAX_CHAR_LIMIT_SUMMARY} character(s)`;
      } else {
        temp.summary = "";
      }
    }

    const isLegal = isLegalJob();

    if (isLegal) {
      if ("seniority" in fieldValues) {
        temp.seniority = fieldValues.seniority ? "" : required;
      }

      if ("employee_count" in fieldValues) {
        temp.employee_count = fieldValues.employee_count ? "" : required;
      }

      if ("billable_hours" in fieldValues) {
        temp.billable_hours = fieldValues.billable_hours ? "" : required;
      }

      if ("bar_status" in fieldValues) {
        temp.bar_status = fieldValues.bar_status ? "" : required;
      }
    } else {
      temp.seniority = "";
      temp.employee_count = "";
      temp.billable_hours = "";
      temp.bar_status = "";
    }

    setErrors({
      ...temp,
    });
    return Object.values(temp).every((x) => x === "");
  };

  const validateAndGoNext = (e) => {
    e.preventDefault();
    if (validate(values)) {
      handleNextStep();
    }
  };

  useEffect(() => {
    const strippedValue = strip(values.summary);
    setCharCount(strippedValue.length);
  }, [values.summary]);

  useEffect(() => {
    if (values.job_title) {
      setJobTitleCharCount(values.job_title.length);
    }
  }, [values.job_title]);

  useEffect(() => {
    fetchAllCategories();
    fetchCompanies("");
  }, []);

  useEffect(() => {
    // Skip on initial mount to prevent resetting values when component first loads
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only reset seniority and employee_count if switching to non-legal category
    // and only if we actually have a category selected
    if (values.category_id) {
      const selectedCategory = categories.find(cat => cat.id === values.category_id);
      if (selectedCategory?.department?.code !== "le") {
        // Only reset if the fields actually have values to avoid unnecessary state updates
        if (values.seniority || values.employee_count || values.billable_hours || values.bar_status) {
          setValues(prev => ({
            ...prev,
            seniority: "",
            employee_count: "",
            billable_hours: "",
            bar_status: ""
          }));
        }
      }
    }
  }, [values.category_id]);

  return (
    <Box>
      <h2>Informations</h2>
      <Grid container spacing={2} component="form" onSubmit={validateAndGoNext}>
        <Grid item xs={12}>
          <Box style={{ display: "flex", rowGap: "8px", alignItems: "start", flexDirection: "column" }}>
            <Box style={{ minWidth: "100px"}}>
              <h3>In what language will you write your ad?</h3>
            </Box>
            <Box style={{ flexGrow: 1, maxWidth: "600px"}}>
              <CardSelect
                fieldName="offer_language"
                selected={values.offer_language}
                values={offerLanguages.map((language) => {
                  return {
                    code: language.code,
                    label: language.value,
                  };
                })}
                onChange={handleCardSelected}
              />
              {errors["offer_language"] && (
                <p
                  className="MuiFormHelperText-root MuiFormHelperText-sizeSmall Mui-error"
                  style={{ color: "#f44336" }}
                >
                  {errors["offer_language"]}
                </p>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box style={{ display: "flex", rowGap: "8px", alignItems: "start", flexDirection: "column" }}>
            <Box style={{ minWidth: "100px"}}>
              <h3>What is the category of the job?</h3>
            </Box>
            <Box style={{ flexGrow: 1 }}>
              {Object.entries(categoriesByDepartment).map(([departmentName, departmentCategories]) => (
                <Box key={departmentName} style={{ marginBottom: "16px" }}>
                  <p style={{ fontSize: "12px", color: "#666", margin: "4px 0" }}>{departmentName}</p>
                  <CardSelect
                    fieldName="category_id"
                    selected={values.category_id}
                    values={departmentCategories.map((category) => ({
                      code: category.id,
                      label: category.name,
                    }))}
                    onChange={handleCardSelected}
                  />
                </Box>
              ))}
              {errors["category_id"] && (
                <p
                  className="MuiFormHelperText-root MuiFormHelperText-sizeSmall Mui-error"
                  style={{ color: "#f44336" }}
                >
                  {errors["category_id"]}
                </p>
              )}
            </Box>
          </Box>
        </Grid>
        {isLegalJob() && (
          <>
            <Grid item xs={12}>
              <Box style={{ display: "flex", rowGap: "8px", alignItems: "start", flexDirection: "column" }}>
                <Box style={{ minWidth: "100px" }}>
                  <h3>What is the seniority level of the job?</h3>
                </Box>
                <Box style={{ flexGrow: 1, maxWidth: "900px" }}>
                  <CardSelect
                    fieldName="seniority"
                    selected={values.seniority}
                    values={seniorityLevels.map((level) => {
                      return {
                        code: level.code,
                        label: level.value,
                      };
                    })}
                    onChange={handleCardSelected}
                  />
                  {errors["seniority"] && (
                    <p
                      className="MuiFormHelperText-root MuiFormHelperText-sizeSmall Mui-error"
                      style={{ color: "#f44336" }}
                    >
                      {errors["seniority"]}
                    </p>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Box style={{ display: "flex", rowGap: "8px", alignItems: "start", flexDirection: "column" }}>
                <Box style={{ minWidth: "100px" }}>
                  <h3>What is the bar status requirement?</h3>
                </Box>
                <Box style={{ flexGrow: 1, maxWidth: "600px" }}>
                  <CardSelect
                    fieldName="bar_status"
                    selected={values.bar_status}
                    values={barStatusOptions.map((status) => {
                      return {
                        code: status.code,
                        label: status.value,
                      };
                    })}
                    onChange={handleCardSelected}
                  />
                  {errors["bar_status"] && (
                    <p
                      className="MuiFormHelperText-root MuiFormHelperText-sizeSmall Mui-error"
                      style={{ color: "#f44336" }}
                    >
                      {errors["bar_status"]}
                    </p>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Box style={{ display: "flex", rowGap: "8px", alignItems: "start", flexDirection: "column" }}>
                <Box style={{ minWidth: "100px" }}>
                  <h3>What is the range for billable hours?</h3>
                </Box>
                <Box style={{ flexGrow: 1, maxWidth: "600px" }}>
                  <CardSelect
                    fieldName="billable_hours"
                    selected={values.billable_hours}
                    values={billableHours.map((hours) => {
                      return {
                        code: hours.code,
                        label: hours.value,
                      };
                    })}
                    onChange={handleCardSelected}
                  />
                  {errors["billable_hours"] && (
                    <p
                      className="MuiFormHelperText-root MuiFormHelperText-sizeSmall Mui-error"
                      style={{ color: "#f44336" }}
                    >
                      {errors["billable_hours"]}
                    </p>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Box style={{ display: "flex", rowGap: "8px", alignItems: "start", flexDirection: "column" }}>
                <Box style={{ minWidth: "100px" }}>
                  <h3>What is the office size?</h3>
                </Box>
                <Box style={{ width: "400px" }}>
                  <TextField
                    style={{ width: "100%" }}
                    size="small"
                    type="number"
                    min="1"
                    value={values.employee_count}
                    name="employee_count"
                    onBlur={handleInputValue}
                    onChange={handleInputValue}
                    placeholder="Number of employees"
                    {...(errors["employee_count"] && {
                      error: true,
                      helperText: errors["employee_count"],
                    })}
                  />
                </Box>
              </Box>
            </Grid>
          </>
        )}
        <Grid item xs={6}>
          <Box style={{ display: "flex", alignItems: "start", flexDirection: "column" }}>
            <h3>What is the job title?</h3>
            <TextField
              fullWidth
              size="small"
              value={values.job_title}
              name="job_title"
              onBlur={handleInputValue}
              onChange={handleInputValue}
              {...(errors["job_title"] && {
                error: true,
              })}
            />
            {jobTitleCharCount > MAX_CHAR_LIMIT_JOB_TITLE ? (
              <Typography style={{ color: "red", fontSize: "14px" }}>
                {`Limit exceeded by ${jobTitleCharCount - MAX_CHAR_LIMIT_JOB_TITLE} character${jobTitleCharCount - MAX_CHAR_LIMIT_JOB_TITLE === 1 ? "" : "s"}`}
              </Typography>
            ) : (
              <Typography style={{ color: "grey", fontSize: "14px" }}>
                {`${MAX_CHAR_LIMIT_JOB_TITLE - jobTitleCharCount} character${(MAX_CHAR_LIMIT_JOB_TITLE - jobTitleCharCount === 0 || MAX_CHAR_LIMIT_JOB_TITLE - jobTitleCharCount === 1) ? "" : "s"} left`}
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box
            style={{
              display: "flex",
              alignItems: "start",
              flexDirection: "column",
            }}
          >
            <h3>For which company?</h3>
            <Autocomplete
              freeSolo
              fullWidth
              value={companyValue}
              inputValue={companyInputValue}
              onInputChange={handleCompanyInputChange}
              onChange={handleCompanySelection}
              filterOptions={(options, params) => {
                const filtered = createFilterOptions()(options, params);

                if (params.inputValue !== "") {
                  filtered.push({
                    inputValue: params.inputValue,
                    name: `Add "${params.inputValue}"`,
                  });
                }

                return filtered.slice(0, 5);
              }}
              getOptionLabel={(option) => {
                if (option.id) {
                  return option.name;
                }
                if (option.inputValue) {
                  return option.inputValue;
                }
                return option.name || "";
              }}
              options={companies}
              renderOption={(props, option) => (
                <li {...props} key={option.id || option.inputValue}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  error={!!errors["company_id"]}
                  helperText={errors["company_id"]}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box style={{ display: "flex", rowGap: "8px", alignItems: "start", flexDirection: "column" }}>
            <Box style={{ minWidth: "100px"}}>
              <h3>What is the job status?</h3>
            </Box>
            <Box style={{ width: "100%" }}>
              <Box style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between" }}>
                {jobStatuses.map((status) => (
                  <CardSelect
                    key={status}
                    fieldName="job_status"
                    selected={values.job_status}
                    values={[{ code: status, label: capitalize(status) }]}
                    onChange={handleCardSelected}
                  />
                ))}
              </Box>
              {errors["job_status"] && (
                <p
                  className="MuiFormHelperText-root MuiFormHelperText-sizeSmall Mui-error"
                  style={{ color: "#f44336" }}
                >
                  {errors["job_status"]}
                </p>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box style={{ display: "flex", rowGap: "8px", alignItems: "start", flexDirection: "column" }}>
            <Box style={{ minWidth: "100px"}}>
              <h3>In which region?</h3>
            </Box>
            <Box style={{ width: "400px" }}>
              <TextField
                select
                style={{ width: "100%" }}
                size="small"
                name="region"
                defaultValue={values.region}
                value={values.region}
                onChange={handleInputValue}
                {...(errors["region"] && {
                  error: true,
                  helperText: errors["region"],
                })}
              >
                {jobRegions.map((region) => {
                  return (
                    <MenuItem value={region} key={`job-d-select-${region}`}>
                      { formatRegion(region) }
                    </MenuItem>
                  );
                })}
              </TextField>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box style={{ display: "flex", rowGap: "8px", alignItems: "start", flexDirection: "column" }}>
            <Box style={{ minWidth: "100px"}}>
              <h3>In which city?</h3>
              <Box style={{ display: "flex" }}>
                <InfoIcon style={{ marginRight: "4px" }}/>
                <span>Write the city in the language of the offer</span>
              </Box>
            </Box>
            <Box style={{ width: "400px" }}>
              <TextField
                style={{ width: "100%" }}
                size="small"
                value={values.city}
                name="city"
                onBlur={handleInputValue}
                onChange={handleInputValue}
                {...(errors["city"] && {
                  error: true,
                  helperText: errors["city"],
                })}
              />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box style={{ display: "flex", rowGap: "8px", alignItems: "start", flexDirection: "column" }}>
            <Box style={{ minWidth: "100px"}}>
              <h3>What are the required languages for the job?</h3>
            </Box>
            <Box style={{ flexGrow: 1, maxWidth: "600px"}}>
              <CardSelect
                fieldName="languages"
                selected={values.languages}
                multiple={true}
                values={languages.map((language) => {
                  return {
                    code: language.code,
                    label: language.value,
                  };
                })}
                onChange={handleCardSelected}
              />
              {errors["languages"] && (
                <p
                  className="MuiFormHelperText-root MuiFormHelperText-sizeSmall Mui-error"
                  style={{ color: "#f44336" }}
                >
                  {errors["languages"]}
                </p>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box style={{ display: "flex", rowGap: "6px", alignItems: "start", flexDirection: "column" }}>
            <Box style={{ minWidth: "100px" }}>
              <h3>What is the salary?</h3>
            </Box>
            <RadioGroup
              value={values.salary_type || "yearly"}
              style={{ display: "flex", flexDirection: "row" }}
              onChange={handleSalaryTypeChange}
            >
              <FormControlLabel value="yearly" control={<Radio />} label="Yearly" />
              <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
              <FormControlLabel value="daily" control={<Radio />} label="Daily" />
              <FormControlLabel value="no-salary" style={{ marginLeft: "2rem" }} control={<Radio />} label="No Salary" />
            </RadioGroup>
            {values.salary_type != "no-salary" &&
              <>
                <Box style={{ width: "50%" }}>
                  <Slider
                    value={[values.min_salary || 0, values.max_salary || 0]}
                    onChange={handleSliderChange}
                    valueLabelDisplay="off"
                    min={0}
                    max={getSliderMax(values.salary_type)}
                    step={getSliderStep(values.salary_type)}
                  />
                </Box>
                <Typography
                  align="center"
                  variant="h6"
                  fontWeight="600"
                  color="#30324D"
                >
                  {formatSalaryInternal(values.salary_type, values.min_salary, values.max_salary, true)}
                </Typography>
                {errors["salary"] && (
                  <p
                    className="MuiFormHelperText-root MuiFormHelperText-sizeSmall Mui-error"
                    style={{ color: "#f44336" }}
                  >
                    {errors["salary"]}
                  </p>
                )}
              </>
            }
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box style={{ display: "flex", rowGap: "8px", alignItems: "start", flexDirection: "column" }}>
            <Box style={{ minWidth: "100px" }}>
              <h3>Write a small summary:</h3>
              <Box style={{ display: "flex" }}>
                <InfoIcon style={{ marginRight: "4px" }} />
                <span>This will be displayed on the small preview of your offer, not on the offer page.</span>
              </Box>
            </Box>
            <Box style={{ flexGrow: 1, width: "100%", height: "200px" }}>
              <TextEditor
                value={values.summary}
                error={errors["summary"]}
                handleChange={(v) => handleTextEditor("summary", v)}
                disabled={charCount > MAX_CHAR_LIMIT_SUMMARY}
              />
            </Box>
            {charCount > MAX_CHAR_LIMIT_SUMMARY ? (
              <Typography style={{ color: "red", fontSize: "14px" }}>
                {`Limit exceeded by ${charCount - MAX_CHAR_LIMIT_SUMMARY} character${charCount - MAX_CHAR_LIMIT_SUMMARY === 1 ? "" : "s"}`}
              </Typography>
            ) : (
              <Typography style={{ color: "grey", fontSize: "14px" }}>
                {`${MAX_CHAR_LIMIT_SUMMARY - charCount} character${(MAX_CHAR_LIMIT_SUMMARY - charCount === 0 || MAX_CHAR_LIMIT_SUMMARY - charCount === 1) ? "" : "s"} left`}
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <hr style={{  marginTop: "16px", marginBottom: "32px" }} />
          <Box style={{ display: "flex", justifyContent: "space-between" }}>
            <Button variant="squared" color="primaryOutlined" onClick={() => window.location.pathname = "/internal"}>Cancel</Button>
            <Button variant="squared" color="primaryContained" type="submit">Next</Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Informations;
